import { Typography, styled } from '@mui/material';
import { FEE_MANAGER } from 'adminConstants/routes';
import { LpFeeStartDate } from 'further-types/lp/lp-fee-template';
import BasicSelect from 'further-ui/components/BasicSelect';
import DatePicker from 'further-ui/components/DatePicker';
import FormField from 'further-ui/components/FormField';
import { investment as rules } from 'further-ui/validations';
import useFundingSourceOptions from 'hooks/form/useFundingSourceOptions';
import useEqualisations from 'hooks/lp/data/equalisations/useEqualisations';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';
import { Controller, useFormContext } from 'react-hook-form';
import { Link } from 'react-router-dom';

const StyledInlineLink = styled(Link)(({ theme }) => ({
  color: theme.palette.secondary.main,
  textDecoration: 'none',
  cursor: 'pointer',
}));

type Props = {
  vehicleId: string;
};

const BaseInvestmentForm: React.FC<Props> = ({ vehicleId }) => {
  const { control, watch } = useFormContext<rules.CreateInvestmentFormValues>();
  const { vehicle } = useVehicle(vehicleId);
  const fundingSourceOptions = useFundingSourceOptions(watch('vehicleId'));
  const dealCompletionDate = watch('dealCompletionDate');

  const hasApprovedFees = vehicle?.lpFeeTemplates?.some(
    (template) =>
      template.hasApprovedFees &&
      template.feeStartDate === LpFeeStartDate.DateOfFirstFundInvestment,
  );
  const { latestApprovedEqualisationDate } = useEqualisations(vehicleId);

  const showFeesNote = Boolean(
    hasApprovedFees &&
      dealCompletionDate &&
      vehicle?.firstInvestmentDate &&
      dealCompletionDate.isBefore(vehicle.firstInvestmentDate),
  );

  return (
    <>
      <FormField
        label="Deal completion date"
        tooltip="Select a target date if the actual deal completion date is unknown at the point of processing. This transaction can be edited after submission to include the actual completion date."
        size="md"
      >
        <Controller
          name="dealCompletionDate"
          control={control}
          render={({ fieldState: { error }, field: { ref, ...rest } }) => (
            <DatePicker
              fullWidth
              error={!!error}
              placeholder="Select deal completion date"
              helperText={error?.message?.toString()}
              minDate={latestApprovedEqualisationDate}
              {...rest}
            />
          )}
        />
      </FormField>
      {showFeesNote && (
        <Typography variant="body2" color="text.secondary">
          <b>Please note:</b> Fees are calculated based on the date of the first
          fund investment, and some fees have already been approved. To enable
          the fees to change to this new "Date of first fund investment", please
          first unapprove the fees{' '}
          <StyledInlineLink to={FEE_MANAGER}>here</StyledInlineLink>. Please
          note that this may require deleting a capital call, if these fees have
          already been called.
        </Typography>
      )}
      <FormField
        label="Funding source"
        tooltip="Select whether this action will be paid through general working capital on account or fresh capital that will be part of a future capital call."
        size="md"
      >
        <Controller
          name="fundingSource"
          control={control}
          render={({ fieldState: { error }, field: { ref, ...rest } }) => (
            <BasicSelect
              fullWidth
              error={!!error}
              options={fundingSourceOptions.options}
              placeholder="Select funding source"
              helperText={error?.message?.toString()}
              disabled={fundingSourceOptions.isLoading}
              {...rest}
            />
          )}
        />
      </FormField>
    </>
  );
};

export default BaseInvestmentForm;
